import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API, idr, pesanGalat } from "../../lib/api";
import { toast } from "sonner";
import { Plus, HandCoins, X, Check, CheckCircle2, AlertTriangle, Clock, FileSpreadsheet, Settings2, Paperclip, Users2, Search, Send, Package, Landmark, Lock, Pencil } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import KategoriManager from "./KategoriManager";
import AttachmentPanel from "./AttachmentPanel";
import OcrScanButton from "./OcrScanButton";
import InputRupiah from "../../components/InputRupiah";
import { hariIniISO, formatTanggal, formatJam } from "../../lib/tanggal";
import { useModalGuard, UnsavedGuardBar } from "../../hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";
import { BatasDaftarNotice } from "./_shared";
import SuratTagihanPrint from "../../components/faktur/SuratTagihanPrint";
import { bolehPutuskanBatal, bolehUbahValidasi, pengajuSama, labelRiwayatValidasi } from "./validasiPiutang";

// Backend `/finance/piutang` mengurut `created_at desc` lalu memotong ke
// `to_list(500)` TANPA parameter urut dari klien (lihat piutang.py) — beda
// dengan `/finance/utang` yang sudah mengurut di server SEBELUM memotong.
// Makanya sort di sini murni client-side atas 500 baris yang terlanjur
// terambil, dan wajib didampingi `BatasDaftarNotice` kalau baris yang
// terambil persis 500 (lihat definisi di ./_shared.jsx).
const PIUTANG_BATAS_SERVER = 500;

const FILTER_TABS = [
  { key: "all", label: "Semua", tone: "slate" },
  { key: "belum_lunas", label: "Belum Lunas", tone: "sky" },
  { key: "jatuh_tempo", label: "Jatuh Tempo", tone: "rose" },
  { key: "lunas", label: "Lunas", tone: "emerald" },
];

const TONE_CLASS = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber:   "bg-amber-50 text-amber-700 border-amber-200",
  orange:  "bg-orange-50 text-orange-700 border-orange-200",
  rose:    "bg-rose-50 text-rose-700 border-rose-200",
  sky:     "bg-sky-50 text-sky-700 border-sky-200",
  slate:   "bg-slate-50 text-slate-600 border-slate-200",
};

const VALID_FILTERS = FILTER_TABS.map(t => t.key);

// Label untuk medan yang datang dari nota. Ditaruh SATU tempat supaya form
// detail dan kolom daftar tidak menampilkan ejaan yang berbeda untuk nilai
// yang sama.
const JENIS_LABEL = { umum: "Pelanggan Umum", b2b: "B2B", user: "USER" };
const TEMPO_LABEL = {
  net_3: "Net 3", net_7: "Net 7", net_10: "Net 10",
  net_30: "Net 30", net_90: "Net 90", deposit: "Deposit",
};

// Label aksi di `verifikasi_log` (brief owner 12 Sep, F7). SATU peta, bukan
// rantai `if` — kalau nanti ada aksi baru yang ditulis backend dan lupa
// didaftarkan di sini, yang bocor ke layar cuma kunci mentahnya (mis.
// "perbaikan_selesai"), dan itu langsung kelihatan salah. Rantai `if` akan
// menampilkan kosong, yang justru tidak kelihatan salah.
const AKSI_VERIFIKASI = {
  verifikasi: "Diverifikasi",
  minta_perbaikan: "Diminta perbaikan",
  perbaikan_selesai: "Perbaikan selesai",
};

/** Nama akun untuk satu baris riwayat pembayaran. Pembayaran BARU menyimpan
 *  `akun_id` (aditif — lihat backend/routers/finance/piutang.py) sehingga
 *  namanya bisa ditarik LIVE dari daftar kas/bank (tetap benar walau akun
 *  itu di-rename belakangan). Pembayaran LAMA sebelum perubahan ini tidak
 *  punya `akun_id` sama sekali — jatuh kembali ke `akun` (nama apa adanya
 *  saat itu dicatat), supaya riwayat lama tetap tampil normal, tidak kosong. */
function namaAkunRiwayat(entry, kasBank) {
  if (entry.akun_id) {
    const found = kasBank.find(a => a.id === entry.akun_id);
    if (found) return found.name;
  }
  return entry.akun || "-";
}

function RiwayatPembayaran({ payments, kasBank, onEdit }) {
  const rows = [...(payments || [])].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  if (rows.length === 0) {
    return <p className="text-xs text-slate-400 py-3 text-center">Belum ada pembayaran tercatat untuk invoice ini.</p>;
  }
  return (
    <table className="w-full text-xs">
      <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-200">
        <tr>
          <th className="text-left py-1.5 px-2">Tanggal</th>
          <th className="text-right py-1.5 px-2">Nominal</th>
          <th className="text-left py-1.5 px-2">Masuk ke Akun</th>
          <th className="text-left py-1.5 px-2">Dicatat Oleh</th>
          {onEdit && <th className="text-right py-1.5 px-2">Aksi</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((p, i) => (
          <tr key={p.txn_id || i} className="border-b border-slate-100 last:border-0">
            <td className="py-1.5 px-2 text-slate-700">{formatTanggal(p.tanggal)}</td>
            <td className="py-1.5 px-2 text-right font-mono text-slate-800">{idr(p.nominal)}</td>
            <td className="py-1.5 px-2 text-slate-700">{namaAkunRiwayat(p, kasBank)}</td>
            <td className="py-1.5 px-2 text-slate-600">
              {p.by || "-"}
              {/* Jejak koreksi ikut terlihat di riwayat — kalau tidak, angka
                  yang berubah setelah masuk buku besar tampak seperti angka
                  yang memang selalu begitu. */}
              {p.dikoreksi_dari && (
                <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100"
                      data-testid={`piu-bayar-dikoreksi-${p.txn_id}`}
                      title={`Semula ${idr(p.dikoreksi_dari.nominal)} tanggal ${p.dikoreksi_dari.tanggal}`
                             + ` — dikoreksi ${p.dikoreksi_dari.oleh}: ${p.dikoreksi_dari.alasan}`}>
                  dikoreksi
                </span>
              )}
            </td>
            {onEdit && (
              <td className="py-1.5 px-2 text-right">
                <button onClick={() => onEdit(p)} data-testid={`piu-bayar-edit-${p.txn_id}`}
                  title="Koreksi pembayaran ini (jurnal lama dibalik, entri baru diposting)"
                  className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:underline">
                  <Pencil className="w-3 h-3" /> Edit
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}


/** F8 (brief owner 12 Sep 2026) — koreksi satu pembayaran piutang.
 *
 *  Bukan "sunting angka": server MEMBALIK jurnal lama dengan entri lawan lalu
 *  memposting yang baru. Modal ini menyebutkannya apa adanya supaya yang
 *  menekan tahu bahwa buku besar akan bertambah dua baris, bukan berubah.
 */
function ModalEditPembayaran({ piutang, bayar, kasBank, onClose, onSelesai }) {
  const [nominal, setNominal] = useState(String(bayar?.nominal ?? ""));
  const [tanggal, setTanggal] = useState(bayar?.tanggal || hariIniISO());
  const [akunId, setAkunId] = useState(bayar?.akun_id || "");
  const [alasan, setAlasan] = useState("");
  const [keterangan, setKeterangan] = useState("");
  const [busy, setBusy] = useState(false);

  const simpan = async () => {
    if (!alasan.trim()) { toast.error("Alasan koreksi wajib diisi"); return; }
    if (!akunId) { toast.error("Pilih akun kas/bank tujuan"); return; }
    if (!(Number(nominal) > 0)) { toast.error("Nominal koreksi harus lebih dari nol"); return; }
    setBusy(true);
    try {
      await api.post(`/finance/piutang/${piutang.id}/pembayaran/${bayar.txn_id}/edit`, {
        nominal: Number(nominal), tanggal, akun_id: akunId,
        keterangan: keterangan.trim(), alasan: alasan.trim(),
      });
      toast.success("Pembayaran dikoreksi — jurnal lama dibalik, entri baru diposting");
      onSelesai();
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal mengoreksi pembayaran"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}
         data-testid="piu-edit-bayar-modal">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full shadow-2xl">
        <div className="p-5 border-b border-slate-100">
          <h3 className="font-display font-bold text-lg">Edit Pembayaran</h3>
          <p className="text-xs text-slate-500">
            {piutang.code} · {piutang.pelanggan_nama} — semula {idr(bayar?.nominal)} pada{" "}
            {formatTanggal(bayar?.tanggal)}
          </p>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-600">
              Nominal
              <input type="number" value={nominal} data-testid="piu-edit-bayar-nominal"
                onChange={e => setNominal(e.target.value)}
                className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm font-mono" />
            </label>
            <label className="text-xs text-slate-600">
              Tanggal
              <input type="date" value={tanggal} data-testid="piu-edit-bayar-tanggal"
                onChange={e => setTanggal(e.target.value)}
                className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
            </label>
          </div>
          <label className="text-xs text-slate-600 block">
            Masuk ke akun
            <select value={akunId} onChange={e => setAkunId(e.target.value)}
              data-testid="piu-edit-bayar-akun"
              className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="">— pilih akun kas/bank —</option>
              {(kasBank || []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-600 block">
            Alasan koreksi <span className="text-rose-600">*</span>
            <input value={alasan} onChange={e => setAlasan(e.target.value)}
              data-testid="piu-edit-bayar-alasan" placeholder="mis. salah ketik nominal, salah akun"
              className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
          </label>
          <label className="text-xs text-slate-600 block">
            Keterangan (opsional)
            <input value={keterangan} onChange={e => setKeterangan(e.target.value)}
              data-testid="piu-edit-bayar-keterangan"
              className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
          </label>
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Jurnal pembayaran lama akan <b>dibalik dengan entri lawan bertanggal sama</b>, lalu
            entri baru diposting. Riwayatnya tetap tersimpan — angka lama tidak dihapus.
            Pembayaran yang sudah dicocokkan di Rekonsiliasi Bank akan ditolak.
          </p>
        </div>
        <div className="p-5 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
          <button onClick={simpan} disabled={busy} data-testid="piu-edit-bayar-simpan"
            className="h-9 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-medium">
            {busy ? "Menyimpan..." : "Simpan Koreksi"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Rincian barang dari nota sumber sebuah piutang.
 *
 *  Tiga keadaan yang SEMUANYA sah dan harus dibedakan di layar:
 *  - `manual`     : piutang diinput tangan, memang tidak punya nota. Bukan error.
 *  - `nota_hilang`: notanya terhapus/dibatalkan tapi piutangnya tinggal. Ini
 *                   HARUS kelihatan — kalau disamarkan jadi "tidak ada rincian",
 *                   tagihan atas transaksi yang sudah dibatalkan bisa lolos.
 *  - `nota`       : ada barangnya.
 */
function RincianBarang({ state }) {
  if (!state) return null;
  if (state.loading) return <p className="text-xs text-slate-400 py-2">Memuat rincian...</p>;
  if (state.error) return <p className="text-xs text-rose-600 py-2">Gagal memuat rincian barang.</p>;
  const d = state.data || {};
  if (d.sumber === "manual") {
    return (
      <p className="text-xs text-slate-500 py-1" data-testid="piu-rincian-manual">
        Piutang ini diinput manual — tidak ada nota penjualan yang bisa dirinci.
      </p>
    );
  }
  if (d.sumber === "nota_hilang") {
    return (
      <p className="text-xs text-rose-700 py-1 font-medium" data-testid="piu-rincian-nota-hilang">
        Nota sumbernya sudah tidak ada di sistem (terhapus atau dibatalkan), tetapi
        piutang ini masih tercatat. Periksa sebelum menagih.
      </p>
    );
  }
  const n = d.nota || {};
  return (
    <div data-testid="piu-rincian-nota">
      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 inline-flex items-center gap-1">
        <Package className="w-3 h-3" /> Rincian Barang — nota {n.code || "-"}
        {n.branch && <span className="font-normal normal-case tracking-normal">· {n.branch}</span>}
        {n.nama_penjual && <span className="font-normal normal-case tracking-normal">· {n.nama_penjual}</span>}
      </p>
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-200">
          <tr>
            <th className="text-left py-1.5 px-2">Barang</th>
            <th className="text-left py-1.5 px-2">IMEI / SKU</th>
            <th className="text-right py-1.5 px-2">Qty</th>
            <th className="text-right py-1.5 px-2">Harga Satuan</th>
            <th className="text-right py-1.5 px-2">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {(d.rincian || []).map((b, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0">
              <td className="py-1.5 px-2 text-slate-800">{b.nama_barang}</td>
              <td className="py-1.5 px-2 text-[10px] font-mono text-slate-500">{b.imei || b.sku || "-"}</td>
              <td className="py-1.5 px-2 text-right">{b.qty}</td>
              <td className="py-1.5 px-2 text-right font-mono text-slate-500">{idr(b.harga_satuan)}</td>
              <td className="py-1.5 px-2 text-right font-mono text-slate-800">{idr(b.subtotal)}</td>
            </tr>
          ))}
          {(d.rincian || []).length === 0 && (
            <tr><td colSpan={5} className="py-3 text-center text-slate-400">Nota ini tidak punya baris barang.</td></tr>
          )}
        </tbody>
        {/* G1 (brief owner 15 Sep 2026). Dulu diskonnya cuma catatan kecil di
            pojok kanan bawah, di LUAR tabel — owner melihat "ada diskon
            Rp 500.000" bersebelahan dengan Nominal Piutang yang belum
            dikurangi, dan wajar menyimpulkan tagihannya salah. Sekarang
            pengurangannya ditampilkan sebagai BARIS tabel sehingga
            "barang - diskon = yang ditagih" terbaca sekali lihat, dan angka
            terakhirnya datang dari backend (`total_setelah_diskon`), bukan
            dihitung ulang di layar. */}
        {n.diskon > 0 && (
          <tfoot className="text-[11px]" data-testid="piu-rincian-diskon">
            <tr className="border-t border-slate-200">
              <td colSpan={4} className="py-1.5 px-2 text-right text-slate-500">Subtotal barang</td>
              <td className="py-1.5 px-2 text-right font-mono text-slate-600">{idr(n.total_harga)}</td>
            </tr>
            <tr>
              <td colSpan={4} className="py-1.5 px-2 text-right text-slate-500">Diskon nota</td>
              <td className="py-1.5 px-2 text-right font-mono text-rose-600">- {idr(n.diskon)}</td>
            </tr>
            <tr className="border-t border-slate-200">
              <td colSpan={4} className="py-1.5 px-2 text-right font-semibold text-slate-700">Nilai ditagih</td>
              <td className="py-1.5 px-2 text-right font-mono font-bold text-slate-900"
                data-testid="piu-rincian-nilai-ditagih">
                {idr(n.total_setelah_diskon)}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}


export default function FinancePiutang() {
  const { can, user } = useAuth();
  // Izin "update" untuk SEMUA aksi yang mengubah piutang (bayar, bayar
  // sekaligus, tagih, verifikasi, kolom Validasi). Kunci yang SAMA dengan
  // backend `piutang.py` sejak brief 22 Sep (B1): gerbang literal role di sana
  // sudah diganti `authorize_module(..., "update")`, jadi tombol Bayar satuan —
  // yang dulu sengaja dibiarkan tanpa gerbang — kini ikut `canPay`. Tanpa itu
  // auditor melihat tombol Bayar yang ujungnya 403 (aturan 3 CLAUDE.md).
  const canPay = can("finance_piutang", "update");
  const [searchParams, setSearchParams] = useSearchParams();
  // KENAPA baca query param: dashboard CEO (kartu AR Overdue) menautkan ke sini
  // lewat `?filter=jatuh_tempo[&highlight=<id>]` supaya klik "AWAS" langsung
  // mendarat di daftar piutang yang relevan. Sebelumnya `filter` HANYA hidup di
  // useState lokal dan tidak pernah membaca URL — tautan itu selalu diabaikan
  // dan mendarat di tab "Semua". `highlight` dipakai untuk menyorot & scroll ke
  // baris piutang spesifik yang dituju dari kartu KPI.
  const urlFilter = searchParams.get("filter");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total_aktif: 0, total_jatuh_tempo: 0, count: 0 });
  // Angka Quick Card datang dari server dan SENGAJA tidak ikut filter yang
  // sedang aktif — ia pengingat yang diklik UNTUK memfilter. Kalau ikut
  // menyusut, "Menunggu Verifikasi: 0" bisa muncul cuma karena sedang membuka
  // tab Lunas, dan tidak ada yang memberitahu masih ada 12 yang menunggu.
  const kartu = stats.kartu || {};
  const [filter, setFilter] = useState(VALID_FILTERS.includes(urlFilter) ? urlFilter : "all");
  const [highlightId, setHighlightId] = useState(searchParams.get("highlight") || null);
  const [showForm, setShowForm] = useState(false);
  const [showBayar, setShowBayar] = useState(null);

  const verifikasi = async (p) => {
    if (!window.confirm(
      `Verifikasi piutang ${p.code}?\n\n` +
      `Pelanggan: ${p.pelanggan_nama}\nNominal: ${idr(p.nominal)}\n` +
      (p.tanggal_jatuh_tempo ? `Jatuh tempo: ${p.tanggal_jatuh_tempo}\n` : "") +
      "\nPastikan datanya sudah COCOK dengan nota cetak. Setelah diverifikasi, " +
      "piutang ini bisa ditagih dan dibayar."
    )) return;
    try {
      const { data } = await api.post(`/finance/piutang/${p.id}/verifikasi`);
      toast.success(data.message || "Piutang diverifikasi ✓");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memverifikasi"); }
  };
  const [showKatMgr, setShowKatMgr] = useState(false);
  const [showAtt, setShowAtt] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [kategori, setKategori] = useState([]);
  const [kasBank, setKasBank] = useState([]);
  const rowRefs = useRef({});

  // Tampilan: daftar invoice per-baris, atau rekap teragregasi per pelanggan (poin 2 brief owner).
  const [viewMode, setViewMode] = useState("invoice"); // "invoice" | "rekap"

  // Riwayat pembayaran per invoice (poin 1) — expand baris, bukan modal terpisah,
  // supaya tetap terlihat konteks invoice-nya (kode, sisa, dst) sambil membaca riwayat.
  const [expandedId, setExpandedId] = useState(null);

  // Bulk penerimaan pembayaran (poin 2) — pola Set yang sama dengan `pages/Assets.jsx`.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBulkBayar, setShowBulkBayar] = useState(false);

  // Pencarian & rentang tanggal — DIKIRIM KE SERVER, tidak menyaring `items`.
  // Daftar ini dipotong di 500 baris oleh backend; menyaring hasil yang sudah
  // terpotong akan membuat piutang di baris ke-501 tidak pernah ketemu, dan
  // layar melaporkan "tidak ada hasil" dengan yakin.
  const [cari, setCari] = useState("");
  const [cariAktif, setCariAktif] = useState("");   // versi ter-debounce yang benar-benar dikirim
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  const [periode, setPeriode] = useState("");     // "" | hari_ini | minggu_ini | bulan_ini | custom
  const [statusUi, setStatusUi] = useState("");   // "" | belum_diverifikasi | ... | lunas
  // Brief 22 Sep (B1): "" | "menunggu_spv" — pengajuan batal validasi yang
  // belum diputuskan. Disaring DI SERVER (alasan yang sama dengan `cari`).
  const [validasiUi, setValidasiUi] = useState("");
  const [detail, setDetail] = useState(null);     // form detail piutang (poin 11)
  const [showRekening, setShowRekening] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setCariAktif(cari.trim()), 350);
    return () => clearTimeout(t);
  }, [cari]);

  // Tagih Sekaligus.
  const [tagihanDoc, setTagihanDoc] = useState(null);
  const [tagihanBusy, setTagihanBusy] = useState(false);
  const [fakturTemplate, setFakturTemplate] = useState(null);

  // Pemicu muat ulang setelah modal (tambah/bayar/kategori/lampiran) ditutup.
  const [reload, setReload] = useState(0);
  const load = () => setReload(n => n + 1);

  // Validasi pencairan (permintaan owner 25 Agu 2026 poin 2). Baris yang sedang
  // dikirim disimpan supaya tombolnya bisa dikunci — tanpa itu, klik ganda
  // mengirim dua permintaan dan yang kedua menimpa hasil yang pertama.
  const [validasiBusy, setValidasiBusy] = useState(null);
  const [investigasiUntuk, setInvestigasiUntuk] = useState(null);
  // Centang validasi yang SUDAH masuk hanya boleh dilepas — atau pengajuan
  // pembatalannya diputuskan — oleh pemegang wewenang SPV piutang (Edit DAN
  // Hapus Piutang). Kunci di sini datang dari helper yang SAMA dengan
  // `IZIN_SPV_PIUTANG` di backend (dijaga tes paritas) — kalau berbeda,
  // tombolnya muncul lalu ujungnya 403 (aturan 3 CLAUDE.md).
  // Staf (Edit tanpa Hapus) memakai tombol "Ajukan batal" (brief 22 Sep).
  const bolehSpv = bolehPutuskanBatal(can);

  const ajukanBatal = async (p) => {
    const alasan = window.prompt(
      `Ajukan pembatalan centang Valid untuk ${p.code}?\n\n` +
      "Piutang tetap dianggap Valid sampai SPV/Manager Finance memutuskan.\n\nAlasan (wajib):");
    if (alasan === null) return;
    if (!alasan.trim()) { toast.error("Alasan wajib diisi"); return; }
    setValidasiBusy(p.id);
    try {
      const { data } = await api.post(`/finance/piutang/${p.id}/validasi/ajukan-batal`, { alasan: alasan.trim() });
      toast.success(data.message || "Pengajuan terkirim — menunggu SPV");
      load();
    } catch (e) { toast.error(pesanGalat(e, "Gagal mengajukan pembatalan")); }
    finally { setValidasiBusy(null); }
  };

  const putuskanBatal = async (p, keputusan) => {
    const peng = p.validasi_pengajuan || {};
    let catatan = "";
    if (keputusan === "setujui") {
      if (!window.confirm(
        `Setujui pembatalan centang Valid ${p.code}?\n\n` +
        `Diajukan: ${peng.oleh || "-"}\nAlasan: ${peng.alasan || "-"}\n\n` +
        "Centang Valid akan dilepas.")) return;
    } else {
      const alasan = window.prompt(
        `Tolak pengajuan batal validasi ${p.code}?\n\n` +
        `Diajukan: ${peng.oleh || "-"}\nAlasan pengaju: ${peng.alasan || "-"}\n\n` +
        "Alasan penolakan (wajib):");
      if (alasan === null) return;
      if (!alasan.trim()) { toast.error("Alasan penolakan wajib diisi"); return; }
      catatan = alasan.trim();
    }
    setValidasiBusy(p.id);
    try {
      const { data } = await api.post(`/finance/piutang/${p.id}/validasi/putuskan-batal`, {
        pengajuan_id: peng.id, keputusan, catatan: catatan || null,
      });
      toast.success(data.message || "Keputusan tersimpan");
      load();
    } catch (e) { toast.error(pesanGalat(e, "Gagal menyimpan keputusan")); }
    finally { setValidasiBusy(null); }
  };

  const kirimValidasi = async (p, status, catatan) => {
    // Melepas centang 'valid' wajib beralasan (ditegakkan juga di backend —
    // ini cuma supaya penolakannya tidak datang sebagai galat mendadak).
    if (p.validasi_status === "valid" && status !== "valid" && !catatan) {
      const alasan = window.prompt(
        `Lepas centang Valid untuk ${p.code}?\n\n` +
        `Alasannya disimpan sebagai riwayat dan bisa dilihat siapa pun nanti.\n\nAlasan:`);
      if (alasan === null) return;
      if (!alasan.trim()) { toast.error("Alasan wajib diisi"); return; }
      catatan = alasan.trim();
    }
    setValidasiBusy(p.id);
    try {
      await api.post(`/finance/piutang/${p.id}/validasi`, { status, catatan: catatan || null });
      toast.success(status === "valid" ? "Ditandai valid ✓"
        : status === "investigasi" ? "Ditandai perlu investigasi" : "Tanda validasi dilepas");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan validasi"); }
    finally { setValidasiBusy(null); }
  };

  // Sinkron ulang kalau URL berubah SETELAH halaman ini sudah ter-mount (mis.
  // navigasi dari link dashboard lain sementara tab piutang masih terbuka di
  // riwayat browser — React Router tidak selalu remount komponen yang sama).
  useEffect(() => {
    const f = searchParams.get("filter");
    if (f && VALID_FILTERS.includes(f) && f !== filter) setFilter(f);
    setHighlightId(searchParams.get("highlight") || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const selectFilter = (key) => {
    setFilter(key);
    setSelectedIds(new Set()); // ganti tab = daftar baris terlihat berubah, seleksi lama tidak relevan lagi
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("filter", key);
      next.delete("highlight"); // ganti tab manual = highlight lama tidak relevan lagi
      return next;
    }, { replace: true });
  };

  // KENAPA BENTUKNYA BEGINI: versi lama menaruh fungsi `load` (yang dibuat
  // ulang tiap render) di dependency useEffect — efek jalan lagi SETIAP
  // render, memicu fetch tanpa henti (layar berkedip), dan respons tab lama
  // yang datang belakangan menimpa tab aktif: piutang "Aktif" bisa nongol di
  // tab Lunas. Flag `batal` memastikan hanya respons request TERAKHIR yang
  // boleh menyentuh state.
  useEffect(() => {
    let batal = false;
    (async () => {
      try {
        const p = new URLSearchParams({ filter });
        if (cariAktif) p.set("q", cariAktif);
        if (periode) p.set("periode", periode);
        if (periode === "custom" || !periode) {
          if (dari) p.set("dari", dari);
          if (sampai) p.set("sampai", sampai);
        }
        if (statusUi) p.set("status_ui", statusUi);
        if (validasiUi) p.set("validasi_ui", validasiUi);
        const r = await api.get(`/finance/piutang?${p.toString()}`);
        if (batal) return;
        setItems(r.data.items || []); setStats(r.data);
      } catch (e) {
        // Rentang tanggal yang belum lengkap diketik (mis. "2026-8") ditolak
        // 400 oleh server. Itu BUKAN kegagalan memuat — menampilkannya sebagai
        // "Gagal memuat piutang" akan membuat pengguna mengira datanya rusak.
        if (batal) return;
        const pesan = e?.response?.data?.detail;
        toast.error(e?.response?.status === 400 && pesan ? pesan : "Gagal memuat piutang");
      }
    })();
    return () => { batal = true; };
  }, [filter, reload, cariAktif, dari, sampai, periode, statusUi, validasiUi]);

  // Scroll otomatis ke baris yang ditautkan dari dashboard, begitu datanya sudah masuk.
  useEffect(() => {
    if (highlightId && rowRefs.current[highlightId]) {
      rowRefs.current[highlightId].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [items, highlightId]);

  // Opsi kategori & akun kas tidak tergantung tab — cukup dimuat sekali dan
  // saat modal ditutup (kategori bisa berubah dari Kelola Kategori).
  useEffect(() => {
    let batal = false;
    (async () => {
      try {
        const [k, kb] = await Promise.all([api.get("/finance/piutang/kategori-options"), api.get("/finance/accounts/kas-bank")]);
        if (batal) return;
        setKategori(k.data.kategori || []); setKasBank(kb.data || []);
      } catch { /* form tetap terbuka dengan pilihan kosong; daftar utama tidak terganggu */ }
    })();
    return () => { batal = true; };
  }, [reload]);

  // Toggle murni. Sejak brief 12 Sep (F6) baris bentangan hanya memuat riwayat
  // pembayaran, dan `payments` SUDAH ikut di respons daftar — jadi tidak ada
  // lagi yang perlu diambil dari `/rincian` di sini. Membentang jadi instan,
  // tanpa jeda "Memuat rincian...".
  const bentangkan = (p) => setExpandedId(expandedId === p.id ? null : p.id);
  // F8: koreksi pembayaran memakai izin BARU `edit_nota_piutang` — bukan
  // `finance_piutang.update` yang dipegang staf untuk mencatat pembayaran
  // biasa. Kunci yang SAMA dengan gerbang backend (aturan 3 CLAUDE.md).
  const bolehEditPembayaran = can("edit_nota_piutang", "update");
  const [editBayar, setEditBayar] = useState(null);   // {piutang, bayar}

  // Form detail piutang (brief poin 11). Sejak brief 12 Sep (F6) inilah
  // SATU-SATUNYA tempat tabel Rincian Barang ditampilkan — baris bentangan
  // "Rincian" cuma memuat riwayat pembayaran.
  const bukaDetail = async (p) => {
    setDetail({ piutang: p, loading: true });
    try {
      const r = await api.get(`/finance/piutang/${p.id}/rincian`);
      setDetail({ ...r.data, loading: false });
    } catch (e) {
      setDetail(null);
      toast.error(e?.response?.data?.detail || "Gagal memuat detail piutang");
    }
  };

  const buatSuratTagihan = async () => {
    setTagihanBusy(true);
    try {
      const r = await api.post("/finance/piutang/dokumen-tagihan", { ids: [...selectedIds] });
      setTagihanDoc(r.data);
      // Kop surat mengikuti template faktur penjualan supaya tidak ada kop
      // kedua yang harus diurus terpisah. Kalau pengguna tidak punya izin
      // faktur, suratnya tetap tercetak dengan kop bawaan — gagal di sini
      // TIDAK boleh membatalkan penagihannya.
      try {
        const t = await api.get("/faktur/templates", { params: { jenis: "penjualan" } });
        setFakturTemplate((t.data?.items || [])[0] || null);
      } catch { /* kop bawaan */ }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyusun surat tagihan");
    } finally { setTagihanBusy(false); }
  };

  // Hanya invoice berstatus "aktif" yang bisa dipilih untuk bulk bayar.
  const visibleAktifIds = useMemo(() => items.filter(p => p.status === "aktif").map(p => p.id), [items]);
  const allVisibleAktifSelected = visibleAktifIds.length > 0 && visibleAktifIds.every(id => selectedIds.has(id));
  const toggleSelectAllVisible = () => setSelectedIds(allVisibleAktifSelected ? new Set() : new Set(visibleAktifIds));
  const toggleSelectOne = (id) => setSelectedIds(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const selectedItems = useMemo(() => items.filter(p => selectedIds.has(p.id)), [items, selectedIds]);
  // Berapa PELANGGAN berbeda yang invoicenya sedang dipilih (brief poin 21).
  // Nama dinormalkan supaya "PT Maju " dan "pt maju" tidak terhitung dua.
  const pelangganTerpilih = useMemo(
    () => [...new Set(selectedItems.map(p => (p.pelanggan_nama || "").trim().toLowerCase()))],
    [selectedItems],
  );
  const selectedTotal = useMemo(
    () => selectedItems.reduce((s, p) => s + ((p.nominal || 0) - (p.nominal_terbayar || 0)), 0),
    [selectedItems],
  );

  // Urutan bawaan (created_at desc dari backend) TIDAK berubah sampai
  // pengguna mengklik salah satu header — `sortKey` null = lewat saja.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedItems = useMemo(() => sortRows(items, sortKey, sortDir, {
    kode: (p) => p.code || "",
    pelanggan: (p) => p.pelanggan_nama || "",
    kategori: (p) => p.kategori || "",
    jatuh_tempo: (p) => p.tanggal_jatuh_tempo || "",
    nominal: (p) => p.nominal || 0,
    sisa: (p) => (p.nominal || 0) - (p.nominal_terbayar || 0),
  }), [items, sortKey, sortDir]);

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-piutang">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Accounts Receivable</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Piutang Usaha</h1>
          <p className="text-sm text-slate-600 mt-1">
            {/* Total Aktif & Jatuh Tempo dihitung server atas SELURUH piutang, bukan
                atas baris yang tampil — kalau daftarnya terpotong, itu dikatakan
                terang-terangan, tidak dibiarkan jadi angka yang diam-diam mengecil. */}
            {stats.terpotong
              ? <>Menampilkan {stats.count} dari {stats.jumlah_total} piutang</>
              : <>{stats.count} piutang</>} · Aktif {idr(stats.total_aktif)} · Jatuh Tempo {idr(stats.total_jatuh_tempo)}
          </p>
          {stats.terpotong && (
            <p className="text-[11px] text-amber-700 mt-0.5" data-testid="piu-terpotong">
              Daftar dipotong di {stats.count} baris. Angka Aktif & Jatuh Tempo di atas tetap menghitung semuanya —
              pakai filter atau pencarian untuk melihat sisanya.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowKatMgr(true)} data-testid="piu-kat-manage" className="h-10 px-3 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5">
            <Settings2 className="w-4 h-4" /> Kelola Kategori
          </button>
          <button onClick={() => setShowRekening(true)} data-testid="piu-rekening-manage"
            title="Rekening tujuan yang dicetak di surat tagihan, per cabang"
            className="h-10 px-3 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5">
            <Landmark className="w-4 h-4" /> Rekening Tagihan
          </button>
          <button onClick={() => setShowExport(true)} data-testid="piu-export" className="h-10 px-3 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-medium inline-flex items-center gap-1.5">
            <FileSpreadsheet className="w-4 h-4" /> Export Excel
          </button>
          {/* Tombol "Tambah Piutang" DIHAPUS (brief 29 Agu poin 2): seluruh
              piutang harus lahir dari nota penjualan bertempo, supaya tidak ada
              tagihan yang tidak bisa ditelusuri ke barang yang keluar toko.
              Endpoint `POST /finance/piutang` sengaja DIBIARKAN hidup — ia masih
              dipakai berkas tes dan jalur pemulihan data; yang dihilangkan
              adalah pintu masuknya bagi pengguna sehari-hari. */}
        </div>
      </div>

      {/* Tampilan: daftar invoice vs rekap per pelanggan (poin 2) */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-1 inline-flex flex-wrap gap-1">
        <button onClick={() => setViewMode("invoice")} data-testid="piu-view-invoice"
          className={`px-4 h-9 rounded-lg text-sm font-medium transition ${viewMode === "invoice" ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:bg-slate-50"}`}>
          Daftar Invoice
        </button>
        <button onClick={() => setViewMode("rekap")} data-testid="piu-view-rekap"
          className={`px-4 h-9 rounded-lg text-sm font-medium transition inline-flex items-center gap-1.5 ${viewMode === "rekap" ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:bg-slate-50"}`}>
          <Users2 className="w-3.5 h-3.5" /> Rekap per Pelanggan
        </button>
      </div>

      {viewMode === "rekap" ? (
        <RekapPelanggan kasBank={kasBank} />
      ) : (
        <>
          {/* Kartu ringkas. Angkanya DARI SERVER atas seluruh piutang yang cocok
              filter, bukan dijumlahkan dari baris yang tampil — kalau daftarnya
              terpotong di 500, kartu ini tetap benar. */}
          <div className={`grid grid-cols-1 gap-3 ${canPay ? "sm:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"}`} data-testid="piu-kartu">
            {[
              { id: "verifikasi", label: "Menunggu Verifikasi",
                nilai: String(kartu.menunggu_verifikasi ?? 0),
                sub: "belum dicocokkan dengan nota cetak",
                tone: "amber", aksi: () => { setStatusUi("belum_diverifikasi"); setPeriode(""); setValidasiUi(""); } },
              { id: "mendekati", label: "Mendekati Jatuh Tempo",
                nilai: String(kartu.jatuh_tempo_7_hari ?? 0),
                sub: `${kartu.jatuh_tempo_3_hari ?? 0} di antaranya dalam 3 hari`,
                tone: "sky", aksi: () => { setStatusUi(""); setPeriode(""); setValidasiUi(""); selectFilter("belum_lunas"); } },
              { id: "belum-terbayar", label: "Total Belum Terbayarkan",
                nilai: idr(kartu.total_belum_terbayar ?? 0),
                sub: "seluruh piutang yang masih punya sisa",
                tone: "rose", aksi: () => { setStatusUi(""); setPeriode(""); setValidasiUi(""); selectFilter("belum_lunas"); } },
              // Brief 22 Sep (B1): pengajuan batal validasi TIDAK muncul di Pusat
              // Approval maupun Telegram — kartu ini satu-satunya penanda bagi
              // SPV. Tab dikembalikan ke "Semua" supaya pengajuan pada piutang
              // lunas tidak tersembunyi oleh tab yang sedang terbuka.
              // Hanya untuk `canPay`: label "Menunggu SPV" tinggal di kolom
              // Validasi yang juga hanya untuk `canPay`. Bagi pemegang view saja,
              // kartu ini menyusutkan daftar tanpa penanda di baris mana pun.
              ...(canPay ? [{ id: "menunggu-spv", label: "Menunggu SPV",
                nilai: String(kartu.menunggu_spv ?? 0),
                sub: "pengajuan batal validasi",
                tone: "violet", aksi: () => { setStatusUi(""); setPeriode(""); setValidasiUi("menunggu_spv"); selectFilter("all"); } }] : []),
            ].map(k => (
              <button key={k.id} onClick={k.aksi} data-testid={`piu-kartu-${k.id}`}
                className="text-left bg-white border border-slate-200 rounded-xl shadow-sm p-4 transition hover:shadow hover:border-slate-300">
                <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{k.label}</p>
                <p className={`font-display text-2xl font-bold mt-1 ${
                  k.tone === "rose" ? "text-rose-700" : k.tone === "amber" ? "text-amber-700"
                    : k.tone === "violet" ? "text-violet-700" : "text-sky-700"}`}>{k.nilai}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{k.sub}</p>
              </button>
            ))}
          </div>

          {/* Pencarian & periode — keduanya dikirim ke SERVER (lihat komentar state). */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-3 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <label className="block text-[11px] font-medium text-slate-500 mb-1">Cari</label>
              <div className="flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200">
                <Search className="w-4 h-4 text-slate-400 shrink-0" />
                <input value={cari} onChange={e => setCari(e.target.value)}
                  data-testid="piu-cari" placeholder="Kode, pelanggan, no HP, keterangan..."
                  className="flex-1 text-sm outline-none bg-transparent" />
                {cari && <button onClick={() => setCari("")} className="text-slate-400 hover:text-slate-700"><X className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1">Periode</label>
              <select value={periode} onChange={e => setPeriode(e.target.value)}
                data-testid="piu-periode" className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                <option value="">Semua tanggal</option>
                <option value="hari_ini">Hari Ini</option>
                <option value="minggu_ini">Minggu Ini</option>
                <option value="bulan_ini">Bulan Ini</option>
                <option value="custom">Custom Tanggal</option>
              </select>
            </div>
            {/* Kolom tanggal hanya muncul untuk "Custom" — kalau tetap terlihat
                saat preset dipilih, pengguna akan mengisinya dan mengira
                filternya bekerja, padahal server memakai preset-nya. */}
            {periode === "custom" && (
              <>
                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">Tanggal dari</label>
                  <input type="date" value={dari} onChange={e => setDari(e.target.value)}
                    data-testid="piu-dari" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">sampai</label>
                  <input type="date" value={sampai} onChange={e => setSampai(e.target.value)}
                    data-testid="piu-sampai" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                </div>
              </>
            )}
            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1">Status</label>
              <select value={statusUi} onChange={e => setStatusUi(e.target.value)}
                data-testid="piu-status" className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                <option value="">Semua status</option>
                <option value="belum_diverifikasi">Belum Diverifikasi</option>
                <option value="aktif">Aktif</option>
                <option value="terbayar_parsial">Terbayar Parsial</option>
                <option value="lunas">Lunas</option>
              </select>
            </div>
            {/* Filter "Menunggu SPV" tidak punya dropdown sendiri — ia dinyalakan
                dari kartu. Chip ini yang memberi tahu bahwa daftar sedang
                tersaring; tanpa chip, daftar yang menyusut terlihat seperti
                data yang hilang. */}
            {validasiUi === "menunggu_spv" && (
              <span data-testid="piu-filter-validasi-chip"
                className="h-10 inline-flex items-center gap-1.5 px-3 rounded-lg bg-violet-50 border border-violet-200 text-violet-700 text-sm">
                Menunggu SPV
                <button onClick={() => setValidasiUi("")} data-testid="piu-filter-validasi-hapus"
                  title="Hapus filter Menunggu SPV" className="text-violet-400 hover:text-violet-700">
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            )}
            {(cari || dari || sampai || periode || statusUi || validasiUi) && (
              <button onClick={() => { setCari(""); setDari(""); setSampai(""); setPeriode(""); setStatusUi(""); setValidasiUi(""); }}
                data-testid="piu-reset-filter"
                className="h-10 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm">
                Reset
              </button>
            )}
          </div>

          {/* Filter Tabs */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-1 inline-flex flex-wrap gap-1">
            {FILTER_TABS.map(t => (
              <button
                key={t.key}
                onClick={() => selectFilter(t.key)}
                data-testid={`piu-filter-${t.key}`}
                className={`px-4 h-9 rounded-lg text-sm font-medium transition ${filter === t.key ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:bg-slate-50"}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Pilih & bayar sekaligus (poin 2) — hanya untuk yang boleh mengubah piutang */}
          {canPay && visibleAktifIds.length > 0 && (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <button onClick={toggleSelectAllVisible} data-testid="piu-select-all"
                className="h-9 px-3 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium">
                {allVisibleAktifSelected ? "Batal Pilih Semua" : "✓ Pilih Semua yang Aktif"}
              </button>
              {selectedIds.size > 0 && (
                <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2" data-testid="piu-bulk-bar">
                  <p className="text-sm text-emerald-800"><b>{selectedIds.size}</b> invoice dipilih · Total {idr(selectedTotal)}</p>
                  <button onClick={() => setShowBulkBayar(true)} data-testid="piu-bulk-pay-open"
                    className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                    Bayar Sekaligus
                  </button>
                  {/* Brief poin 21-22: satu surat = SATU pelanggan. Tombolnya
                      dimatikan kalau yang dipilih dari beberapa pelanggan, dan
                      backend juga menolaknya (aturan 2 CLAUDE.md) — mencampur
                      dua pelanggan dalam satu surat berarti memberitahu yang
                      satu berapa utang yang lain. */}
                  <button onClick={buatSuratTagihan} disabled={tagihanBusy || pelangganTerpilih.length !== 1}
                    data-testid="piu-tagih-bulk"
                    title={pelangganTerpilih.length > 1
                      ? `Invoice yang dipilih milik ${pelangganTerpilih.length} pelanggan berbeda — pilih satu pelanggan saja`
                      : "Susun surat tagihan untuk invoice yang dipilih"}
                    className="h-9 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium inline-flex items-center gap-1.5">
                    <Send className="w-3.5 h-3.5" /> {tagihanBusy ? "Menyusun..." : "Tagih Sekaligus"}
                  </button>
                  {pelangganTerpilih.length > 1 && (
                    <p className="text-[11px] text-rose-700 font-medium" data-testid="piu-tagih-beda-pelanggan">
                      {pelangganTerpilih.length} pelanggan berbeda terpilih
                    </p>
                  )}
                  <button onClick={() => setSelectedIds(new Set())} className="text-xs text-slate-500 hover:underline">Batal</button>
                </div>
              )}
            </div>
          )}

          <BatasDaftarNotice shown={items.length} batas={PIUTANG_BATAS_SERVER} />

          {/* Table */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    {canPay && <th className="px-3 py-2.5 text-center w-8"></th>}
                    <SortTh label="Kode / Tanggal" sortKeyName="kode" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="piu-sort-kode" />
                    <SortTh label="Pelanggan" sortKeyName="pelanggan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="piu-sort-pelanggan" />
                    <SortTh label="Kategori" sortKeyName="kategori" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="piu-sort-kategori" />
                    <SortTh label="Jatuh Tempo" sortKeyName="jatuh_tempo" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="piu-sort-jatuh-tempo" />
                    <th className="px-4 py-2.5 text-left">Umur Piutang</th>
                    <th className="px-4 py-2.5 text-left">PIC</th>
                    <SortTh label="Nominal" sortKeyName="nominal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="piu-sort-nominal" />
                    <SortTh label="Sisa" sortKeyName="sisa" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="piu-sort-sisa" />
                    <th className="px-4 py-2.5 text-right">Selisih</th>
                    <th className="px-4 py-2.5 text-center">Status</th>
                    {canPay && <th className="px-4 py-2.5 text-center">Validasi</th>}
                    <th className="px-4 py-2.5 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan={canPay ? 13 : 11} className="p-10 text-center text-sm text-slate-400">Belum ada piutang di filter ini</td></tr>
                  ) : sortedItems.map(p => {
                    const sisa = (p.nominal || 0) - (p.nominal_terbayar || 0);
                    const attCount = (p.attachments || []).length;
                    const payCount = (p.payments || []).length;
                    const isHighlighted = p.id === highlightId;
                    const isExpanded = expandedId === p.id;
                    return (
                      <Fragment key={p.id}>
                        <tr
                          ref={el => { if (el) rowRefs.current[p.id] = el; }}
                          data-testid={isHighlighted ? "piu-row-highlighted" : undefined}
                          className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors ${isHighlighted ? "bg-amber-50 ring-1 ring-inset ring-amber-300" : ""}`}
                        >
                          {canPay && (
                            <td className="px-3 py-3 text-center">
                              {p.status === "aktif" && (
                                <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleSelectOne(p.id)}
                                  data-testid={`piu-row-check-${p.id}`} className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                              )}
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <p className="font-mono text-xs text-slate-500">{p.code}</p>
                            <p className="text-xs text-slate-600">{p.tanggal}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900">{p.pelanggan_nama}</p>
                            {p.no_hp && <p className="text-[10px] text-slate-500">{p.no_hp}</p>}
                          </td>
                          <td className="px-4 py-3 text-slate-700 text-xs">{p.kategori}</td>
                          <td className="px-4 py-3 text-xs text-slate-700">
                            {p.tanggal_jatuh_tempo || "-"}
                          </td>
                          {/* Umur Piutang = kolom TERSENDIRI di sebelah kanan Jatuh
                              Tempo (brief poin 4). Sebelumnya ia menumpang di sel
                              Jatuh Tempo sebagai baris kecil, jadi tidak bisa
                              dibaca sebagai kolom dan tidak bisa dibandingkan
                              antarbaris. */}
                          <td className="px-4 py-3 text-xs" data-testid={`piu-umur-${p.id}`}>
                            <span className={p.sla_status === "terlambat" ? "text-rose-600 font-semibold"
                              : p.sla_status === "jatuh_tempo_hari_ini" ? "text-amber-600 font-semibold"
                              : "text-slate-500"}>
                              {p.sla_label || "-"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-600" data-testid={`piu-pic-${p.id}`}>
                            {p.pic_nota || p.created_by_name || "-"}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-slate-800">{idr(p.nominal)}</td>
                          <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{idr(sisa)}</td>
                          {/* Selisih = tagihan - yang benar-benar diterima, dalam rupiah DAN
                              persen. Persennya yang membuat Rp 100.000 di tagihan Rp 1 juta
                              (10%) terbaca beda dari Rp 100.000 di tagihan Rp 50 juta (0,2%). */}
                          <td className="px-4 py-3 text-right" data-testid={`piu-selisih-${p.id}`}>
                            {(p.selisih_nominal || 0) === 0
                              ? <span className="text-xs text-slate-400">-</span>
                              : <>
                                  <p className="font-mono text-xs text-rose-600 font-semibold">{idr(p.selisih_nominal)}</p>
                                  <p className="text-[10px] text-rose-400">{(p.selisih_persen || 0).toFixed(1)}%</p>
                                </>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <StatusPill s={p.status_tampilan || p.status} />
                            {/* Progres pembayaran (poin 9) — hanya bermakna selama piutang
                                masih aktif (0% "belum ditagih" s/d >0% "terbayar sebagian"). */}
                            {p.status === "aktif" && (
                              <div className="mt-1 w-16 mx-auto" data-testid={`piu-progres-${p.id}`}>
                                <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-1 bg-emerald-400" style={{ width: `${p.persen_terbayar || 0}%` }} />
                                </div>
                                <p className="text-[9px] text-slate-400 mt-0.5">{(p.persen_terbayar || 0).toFixed(0)}%</p>
                              </div>
                            )}
                          </td>
                          {/* Validasi pencairan: centang kalau uang yang masuk sudah sesuai,
                              atau tandai perlu investigasi (wajib pakai alasan). Murni
                              penandaan pemeriksaan — tidak menyentuh nominal maupun jurnal. */}
                          {canPay && (
                            <td className="px-4 py-3 text-center" data-testid={`piu-validasi-${p.id}`}>
                              {p.validasi_status === "valid" && p.validasi_menunggu_spv ? (
                                // Brief 22 Sep (B1): pengajuan batal menunggu SPV.
                                // Piutang TETAP valid; semua transisi lain dikunci
                                // backend sampai diputuskan. Setujui/Tolak SELALU
                                // terlihat (bukan hover) — di tablet tidak ada hover.
                                // Diperiksa SEBELUM status piutang: backend tetap menerima
                                // keputusan atas pengajuan pada piutang yang kemudian dibatalkan
                                // (nota diedit jadi tunai) — tanpa tombol ini pengajuannya
                                // menggantung selamanya.
                                <div className="flex flex-col items-center gap-1">
                                  <span data-testid={`piu-validasi-menunggu-${p.id}`}
                                        title={`Diajukan ${p.validasi_pengajuan?.oleh || "-"}${p.validasi_pengajuan?.pada ? ` · ${formatTanggal(p.validasi_pengajuan.pada, { denganJam: true })}` : ""} — ${p.validasi_pengajuan?.alasan || ""}`}
                                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-violet-50 text-violet-700 border border-violet-200 cursor-default whitespace-nowrap">
                                    <Clock className="w-3 h-3" /> Valid · Menunggu SPV
                                  </span>
                                  {bolehSpv && !pengajuSama(p.validasi_pengajuan, user) ? (
                                    <div className="flex items-center gap-1">
                                      <button onClick={() => putuskanBatal(p, "setujui")} disabled={validasiBusy === p.id}
                                              data-testid={`piu-validasi-setujui-${p.id}`}
                                              className="text-[11px] px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                                        Setujui
                                      </button>
                                      <button onClick={() => putuskanBatal(p, "tolak")} disabled={validasiBusy === p.id}
                                              data-testid={`piu-validasi-tolak-${p.id}`}
                                              className="text-[11px] px-2 py-0.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                                        Tolak
                                      </button>
                                    </div>
                                  ) : bolehSpv ? (
                                    // Pengaju yang juga SPV tidak boleh memutuskan
                                    // pengajuannya sendiri — backend menolaknya.
                                    <span data-testid={`piu-validasi-menunggu-lain-${p.id}`}
                                          className="text-[10px] text-slate-400">menunggu SPV lain</span>
                                  ) : null}
                                </div>
                              ) : !bolehUbahValidasi(p) ? (
                                // Piutang di luar aktif/lunas (belum diverifikasi, pending, atau
                                // dibatalkan lewat edit nota yang TIDAK menyentuh centangnya):
                                // backend menolak setiap perubahan validasi di sini (400), jadi
                                // hanya tandanya yang terlihat — tanpa tombol yang pasti gagal.
                                <span data-testid={`piu-validasi-nonaktif-${p.id}`}
                                      title="Validasi pencairan hanya bisa diubah selama piutang aktif/lunas"
                                      className="inline-flex flex-col items-center gap-0.5 cursor-default">
                                  {p.validasi_status === "valid" ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      <Check className="w-3 h-3" /> Valid <Lock className="w-2.5 h-2.5 opacity-60" />
                                    </span>
                                  ) : p.validasi_status === "investigasi" ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                      <AlertTriangle className="w-3 h-3" /> Investigasi
                                    </span>
                                  ) : (
                                    <span className="text-[11px] text-slate-300">—</span>
                                  )}
                                  {(p.validasi_status === "valid" || p.validasi_status === "investigasi") && (
                                    <span className="text-[10px] text-slate-400">
                                      {p.status === "dibatalkan" ? "piutang dibatalkan" : "piutang belum aktif"}
                                    </span>
                                  )}
                                </span>
                              ) : p.validasi_status === "investigasi" ? (
                                <button onClick={() => kirimValidasi(p, "belum")} disabled={validasiBusy === p.id}
                                        data-testid={`piu-validasi-lepas-${p.id}`}
                                        title={p.validasi_catatan ? `${p.validasi_catatan} — ${p.validasi_oleh}` : "Klik untuk melepas tanda"}
                                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 disabled:opacity-50">
                                  <AlertTriangle className="w-3 h-3" /> Investigasi
                                </button>
                              ) : p.validasi_status === "valid" ? (
                                // `formatTanggal`, BUKAN `.slice(0,10)`:
                                // `validasi_pada` ISO ber-offset UTC, jadi
                                // memotong 10 huruf pertama menampilkan tanggal
                                // KEMARIN antara 00:00-07:00 WIB.
                                bolehSpv ? (
                                  <button onClick={() => kirimValidasi(p, "belum")} disabled={validasiBusy === p.id}
                                          data-testid={`piu-validasi-lepas-${p.id}`}
                                          title={`Divalidasi ${p.validasi_oleh}${p.validasi_pada ? ` · ${formatTanggal(p.validasi_pada, { denganJam: true })}` : ""} — klik untuk melepas (wajib beralasan)`}
                                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 disabled:opacity-50">
                                    <Check className="w-3 h-3" /> Valid
                                  </button>
                                ) : (
                                  <div className="flex flex-col items-center gap-1">
                                    <span data-testid={`piu-validasi-terkunci-${p.id}`}
                                          title={`Divalidasi ${p.validasi_oleh}${p.validasi_pada ? ` · ${formatTanggal(p.validasi_pada, { denganJam: true })}` : ""} — pembatalan centang perlu persetujuan SPV/Manager Finance`}
                                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default">
                                      <Check className="w-3 h-3" /> Valid <Lock className="w-2.5 h-2.5 opacity-60" />
                                    </span>
                                    <button onClick={() => ajukanBatal(p)} disabled={validasiBusy === p.id}
                                            data-testid={`piu-validasi-ajukan-${p.id}`}
                                            title="Ajukan pembatalan centang Valid ke SPV/Manager Finance (wajib beralasan)"
                                            className="text-[10px] text-slate-500 hover:text-violet-700 underline decoration-dotted disabled:opacity-50">
                                      Ajukan batal
                                    </button>
                                  </div>
                                )
                              ) : (
                                <div className="flex items-center justify-center gap-1.5">
                                  <label className="inline-flex items-center gap-1 text-[11px] text-slate-500 cursor-pointer" title="Tandai valid">
                                    <input type="checkbox" checked={false} disabled={validasiBusy === p.id}
                                           onChange={() => kirimValidasi(p, "valid")}
                                           data-testid={`piu-validasi-valid-${p.id}`}
                                           className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                                    Valid
                                  </label>
                                  <button onClick={() => setInvestigasiUntuk(p)} disabled={validasiBusy === p.id}
                                          data-testid={`piu-validasi-investigasi-${p.id}`} title="Tandai perlu investigasi"
                                          className="text-slate-300 hover:text-amber-600 disabled:opacity-50">
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )}
                            </td>
                          )}
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button onClick={() => setShowAtt(p)} data-testid={`piu-att-${p.id}`} title="Kelola Lampiran" className="mr-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-700">
                              <Paperclip className="w-3.5 h-3.5" /> {attCount > 0 && attCount}
                            </button>
                            <button onClick={() => bukaDetail(p)} data-testid={`piu-detail-${p.id}`}
                              title="Form detail piutang" className="mr-2 text-xs font-medium text-sky-700 hover:underline">
                              Detail
                            </button>
                            <button onClick={() => bentangkan(p)} data-testid={`piu-riwayat-toggle-${p.id}`}
                              title="Riwayat pembayaran" className="mr-2 text-xs text-slate-500 hover:text-emerald-700">
                              Rincian{payCount > 0 && ` (${payCount})`}
                            </button>
                            {canPay && p.status === "aktif" && (
                              <button onClick={() => setShowBayar(p)} data-testid={`piu-bayar-${p.id}`} className="text-xs font-medium text-emerald-700 hover:underline">Bayar</button>
                            )}
                            {/* Tombol Bayar & Tagih SENGAJA tidak muncul untuk
                                piutang yang belum diverifikasi (poin 6) —
                                backend juga menolaknya, jadi ini bukan sekadar
                                menyembunyikan tombol. */}
                            {canPay && ["belum_diverifikasi", "perlu_perbaikan"].includes(p.status) && (
                              <>
                                <button onClick={() => verifikasi(p)} data-testid={`piu-verifikasi-${p.id}`}
                                  className="text-xs font-medium text-violet-700 hover:underline">
                                  Verifikasi
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr data-testid={`piu-riwayat-row-${p.id}`}>
                            <td colSpan={canPay ? 13 : 11} className="px-4 py-3 bg-slate-50/60 border-b border-slate-100">
                              {/* Brief owner 12 Sep (F6): baris bentangan "Rincian"
                                  SENGAJA hanya berisi riwayat pembayaran. Tabel
                                  Rincian Barang pindah sepenuhnya ke modal Detail
                                  supaya tidak tampil dobel. Catatan: alarm "nota
                                  sumbernya sudah tidak ada" ikut pindah ke sana —
                                  itu konsekuensi yang disadari, bukan regresi. */}
                              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Riwayat Pembayaran</p>
                              <RiwayatPembayaran payments={p.payments} kasBank={kasBank}
                                onEdit={bolehEditPembayaran
                                  ? (bayar) => setEditBayar({ piutang: p, bayar })
                                  : undefined} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showForm && <FormPiutang kategori={kategori} onClose={() => { setShowForm(false); load(); }} />}
      {showBayar && <FormBayar piutang={showBayar} kasBank={kasBank} onClose={() => { setShowBayar(null); load(); }} />}
      {/* Form detail piutang — sepuluh informasi yang diminta brief poin 11,
          plus tabel Rincian Barang (Tipe Barang, IMEI, Nominal per Produk)
          supaya Finance bisa mencocokkan data aplikasi dengan nota CETAK tanpa
          berpindah modul. */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4"
             onClick={() => setDetail(null)} data-testid="piu-detail-modal">
          <div onClick={e => e.stopPropagation()}
               className="bg-white rounded-2xl max-w-3xl w-full max-h-[88vh] flex flex-col shadow-2xl">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-display font-bold text-lg">Detail Piutang</h3>
                <p className="text-xs text-slate-500 font-mono">{detail.piutang?.code}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-5">
              {(() => {
                const p = detail.piutang || {};
                const n = detail.nota || {};
                const sisa = (p.nominal || 0) - (p.nominal_terbayar || 0);
                const baris = [
                  ["No Invoice", p.code],
                  ["Tanggal", formatTanggal(p.tanggal)],
                  ["Nama Pelanggan", p.pelanggan_nama],
                  ["Jenis Pelanggan", JENIS_LABEL[p.jenis_pelanggan] || p.jenis_pelanggan || "Umum"],
                  ["Metode Pembayaran", TEMPO_LABEL[p.tempo_pembayaran] || p.tempo_pembayaran || "Tunai"],
                  ["Nominal Piutang", idr(p.nominal)],
                  ["Sudah Dibayar", idr(p.nominal_terbayar || 0)],
                  ["Sisa", idr(sisa)],
                  ["Jatuh Tempo", p.tanggal_jatuh_tempo ? formatTanggal(p.tanggal_jatuh_tempo) : "-"],
                  ["Umur Piutang", p.sla_label || "-"],
                  ["PIC (pembuat nota)", p.pic_nota || p.created_by_name || "-"],
                  // Jejak verifikasi (brief owner 12 Sep, F7). Ketiganya ditulis
                  // TERPISAH karena owner menyebutnya satu per satu. `verifikasi_pada`
                  // disimpan sebagai ISO ber-offset (`now_iso()`), jadi WAJIB lewat
                  // formatTanggal/formatJam yang mengonversinya ke Asia/Jakarta —
                  // `.slice(0,10)` akan menampilkan tanggal UTC, yaitu tanggal KEMARIN
                  // antara 00:00-07:00 WIB.
                  ["Diverifikasi oleh", p.verifikasi_oleh || "-"],
                  ["Tanggal Verifikasi", p.verifikasi_pada ? formatTanggal(p.verifikasi_pada) : "-"],
                  ["Waktu Verifikasi", p.verifikasi_pada ? `${formatJam(p.verifikasi_pada)} WIB` : "-"],
                ];
                return (
                  <>
                    <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3">
                      {baris.map(([k, v]) => (
                        <div key={k}>
                          <dt className="text-[11px] uppercase tracking-wide text-slate-500">{k}</dt>
                          <dd className="text-sm text-slate-900 mt-0.5">{v || "-"}</dd>
                        </div>
                      ))}
                      <div>
                        <dt className="text-[11px] uppercase tracking-wide text-slate-500">Status Piutang</dt>
                        <dd className="mt-0.5"><StatusPill s={p.status_tampilan || p.status} /></dd>
                      </div>
                    </dl>
                    {n.code && (
                      <p className="text-[11px] text-slate-500">
                        Dari nota <span className="font-mono">{n.code}</span>
                        {n.branch && ` · ${n.branch}`}{n.nama_penjual && ` · ${n.nama_penjual}`}
                      </p>
                    )}
                    {/* Riwayat verifikasi (brief owner 12 Sep, F7): "harus tersimpan
                        sebagai riwayat/audit". Seksinya SEMBUNYI kalau kosong —
                        piutang manual & dokumen lama memang tidak punya log, itu
                        normal, bukan data rusak. */}
                    {(p.verifikasi_log || []).length > 0 && (
                      <div className="border-t border-slate-100 pt-4" data-testid="piu-detail-verifikasi-log">
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
                          Riwayat Verifikasi
                        </p>
                        <ul className="space-y-1.5">
                          {p.verifikasi_log.map((v, i) => (
                            <li key={i} className="text-xs text-slate-700 flex flex-wrap gap-x-2">
                              <span className="font-medium">{AKSI_VERIFIKASI[v.aksi] || v.aksi}</span>
                              <span className="text-slate-500">oleh {v.oleh || v.email || "-"}</span>
                              <span className="text-slate-400">
                                {v.pada ? formatTanggal(v.pada, { denganJam: true }) : "-"}
                              </span>
                              {(v.alasan || v.catatan) && (
                                <span className="text-slate-500 basis-full">— {v.alasan || v.catatan}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {/* Brief 22 Sep (B1): pengajuan yang sedang menunggu ditampilkan
                        TERANG di sini — tooltip di sel tabel tidak terlihat di
                        perangkat sentuh. Tidak ada tombol aksi di modal: satu tempat
                        aksi (baris tabel) berarti satu set gerbang. */}
                    {p.validasi_pengajuan && (
                      <div data-testid="piu-detail-validasi-menunggu"
                           className="text-xs text-violet-800 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
                        <b>Menunggu SPV:</b> pembatalan centang Valid diajukan{" "}
                        {p.validasi_pengajuan.oleh || "-"}
                        {p.validasi_pengajuan.pada && ` · ${formatTanggal(p.validasi_pengajuan.pada, { denganJam: true })}`}
                        {p.validasi_pengajuan.alasan && <> — {p.validasi_pengajuan.alasan}</>}
                      </div>
                    )}
                    {/* Riwayat Validasi (brief 22 Sep, B1): siapa mencentang, siapa
                        mengajukan batal, siapa memutuskan. Pola sama dengan Riwayat
                        Verifikasi — disembunyikan kalau kosong. Entri lama tanpa
                        `aksi` dilabeli dari dari→ke, tanpa migrasi data. */}
                    {(p.validasi_log || []).length > 0 && (
                      <div className="border-t border-slate-100 pt-4" data-testid="piu-detail-validasi-log">
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
                          Riwayat Validasi
                        </p>
                        <ul className="space-y-1.5">
                          {p.validasi_log.map((v, i) => (
                            <li key={i} className="text-xs text-slate-700 flex flex-wrap gap-x-2">
                              <span className="font-medium">{labelRiwayatValidasi(v)}</span>
                              <span className="text-slate-500">oleh {v.oleh || v.email || "-"}</span>
                              <span className="text-slate-400">
                                {v.pada ? formatTanggal(v.pada, { denganJam: true }) : "-"}
                              </span>
                              {v.diajukan_oleh && (
                                <span className="text-slate-500">· diajukan {v.diajukan_oleh}</span>
                              )}
                              {(v.alasan_pengajuan || v.catatan) && (
                                <span className="text-slate-500 basis-full">
                                  — {v.alasan_pengajuan && v.catatan && v.alasan_pengajuan !== v.catatan
                                    ? `${v.alasan_pengajuan} | ${v.catatan}`
                                    : (v.catatan || v.alasan_pengajuan)}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="border-t border-slate-100 pt-4">
                      {detail.loading
                        ? <p className="text-xs text-slate-400">Memuat rincian...</p>
                        : <RincianBarang state={{ data: detail }} />}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Surat tagihan hasil "Tagih Sekaligus". Dicetak lewat mesin `cetak()`
          yang sama dengan faktur — memakai window.print() mentah akan
          melahirkan kembali bug "keluar 3-4 rangkap". */}
      {tagihanDoc && (
        <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto p-4" data-testid="piu-tagihan-modal">
          <div className="bg-white rounded-2xl max-w-4xl w-full mx-auto my-4 shadow-2xl">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
              <div>
                <h3 className="font-display font-bold text-lg">Surat Tagihan</h3>
                <p className="text-xs text-slate-500">
                  {tagihanDoc.pelanggan?.length} pelanggan · {tagihanDoc.jumlah_invoice} invoice
                </p>
              </div>
              <button onClick={() => { setTagihanDoc(null); setSelectedIds(new Set()); }}
                className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              <SuratTagihanPrint dokumen={tagihanDoc} template={fakturTemplate} />
            </div>
          </div>
        </div>
      )}

      {showRekening && <RekeningCabangModal onClose={() => setShowRekening(false)} />}
      {editBayar && (
        <ModalEditPembayaran piutang={editBayar.piutang} bayar={editBayar.bayar} kasBank={kasBank}
          onClose={() => setEditBayar(null)}
          onSelesai={() => { setEditBayar(null); load(); }} />
      )}

      {showKatMgr && <KategoriManager jenis="piutang" title="Kelola Kategori Piutang" onClose={() => { setShowKatMgr(false); load(); }} />}
      {showAtt && <AttachmentPanel module="piutang" objId={showAtt.id} objLabel={`${showAtt.code} · ${showAtt.pelanggan_nama}`} onClose={() => { setShowAtt(null); load(); }} />}
      {showExport && <ExportDialog module="piutang" onClose={() => setShowExport(false)} />}
      {investigasiUntuk && (
        <ModalInvestigasi
          piutang={investigasiUntuk}
          onBatal={() => setInvestigasiUntuk(null)}
          onKirim={async (catatan) => { await kirimValidasi(investigasiUntuk, "investigasi", catatan); setInvestigasiUntuk(null); }}
        />
      )}
      {showBulkBayar && (
        <BulkBayarModal
          items={selectedItems}
          kasBank={kasBank}
          onClose={() => { setShowBulkBayar(false); setSelectedIds(new Set()); }}
          onDone={load}
        />
      )}
    </div>
  );
}

// ============ Bulk penerimaan pembayaran (poin 2) ============
function BulkBayarModal({ items, kasBank, onClose, onDone }) {
  const [rows, setRows] = useState(() => items.map(p => {
    const sisa = (p.nominal || 0) - (p.nominal_terbayar || 0);
    return { id: p.id, code: p.code, pelanggan_nama: p.pelanggan_nama, sisa, nominal: sisa };
  }));
  const [form, setForm] = useState({ tanggal: hariIniISO(), sumber_akun_id: "", keterangan: "" });
  const [busy, setBusy] = useState(false);
  // Snapshot baris yang BENAR-BENAR dikirim ke backend — dipisah dari `rows`
  // karena kasir masih bisa membuang baris (X) sebelum submit; hasil per-item
  // dari backend harus dicocokkan ke snapshot ini, bukan `rows` yang bisa
  // sudah berubah oleh state lain.
  const [submitted, setSubmitted] = useState([]);
  const [results, setResults] = useState(null);

  const removeRow = (id) => setRows(prev => prev.filter(r => r.id !== id));
  const updNominal = (id, n) => setRows(prev => prev.map(r => (r.id === id ? { ...r, nominal: n } : r)));
  const total = rows.reduce((s, r) => s + (Number(r.nominal) || 0), 0);

  const submit = async () => {
    if (rows.length === 0) { toast.error("Pilih minimal 1 invoice untuk dibayar 😊"); return; }
    if (!form.sumber_akun_id) { toast.error("Pilih akun tujuan dulu 😊"); return; }
    setBusy(true);
    setSubmitted(rows);
    try {
      const r = await api.post("/finance/piutang/bayar-bulk", {
        items: rows.map(row => ({ id: row.id, nominal: Number(row.nominal) })),
        sumber_akun_id: form.sumber_akun_id, tanggal: form.tanggal, keterangan: form.keterangan,
      });
      setResults(r.data.results || []);
      if (r.data.failed_count > 0) toast.warning(r.data.message);
      else toast.success(r.data.message);
      onDone(); // refresh daftar di belakang layar — modal tetap terbuka untuk menampilkan hasil per invoice
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memproses pembayaran sekaligus"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="piu-bulk-modal">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><HandCoins className="w-5 h-5 text-emerald-600" /> Bayar Sekaligus ({rows.length} invoice)</h3>
        <p className="text-xs text-slate-500 mb-4">Tiap invoice tetap dicatat & dijurnal SENDIRI-SENDIRI — bulk ini cuma menghemat entri berulang.</p>

        {!results ? (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Field label="Tanggal"><input type="date" value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })} className="input" data-testid="piu-bulk-tgl" /></Field>
              <Field label="Masuk ke Akun"><select value={form.sumber_akun_id} onChange={e => setForm({ ...form, sumber_akun_id: e.target.value })} className="input" data-testid="piu-bulk-akun"><option value="">Pilih...</option>{kasBank.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></Field>
              <Field label="Keterangan (opsional)" col2><input value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} className="input" data-testid="piu-bulk-ket" /></Field>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden mb-3">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Invoice</th>
                    <th className="text-left px-3 py-2">Pelanggan</th>
                    <th className="text-right px-3 py-2">Sisa</th>
                    <th className="text-right px-3 py-2">Bayar</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-slate-400">Semua invoice dibuang dari batch ini</td></tr>
                  ) : rows.map(r => (
                    <tr key={r.id} className="border-t border-slate-50">
                      <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                      <td className="px-3 py-2">{r.pelanggan_nama}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500">{idr(r.sisa)}</td>
                      <td className="px-3 py-2 text-right">
                        <InputRupiah value={r.nominal} onChange={n => updNominal(r.id, n)} className="w-32 h-8 px-2 border border-slate-200 rounded text-right text-sm" testid={`piu-bulk-item-nominal-${r.id}`} />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <button onClick={() => removeRow(r.id)} data-testid={`piu-bulk-item-remove-${r.id}`} className="text-slate-400 hover:text-rose-600"><X className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-right font-semibold text-slate-800 mb-4">Total: {idr(total)}</p>
            <div className="flex gap-2">
              <button onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm">Batal</button>
              <button onClick={submit} disabled={busy || rows.length === 0} data-testid="piu-bulk-submit" className="flex-1 h-10 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50">
                {busy ? "Memproses..." : `Bayar ${rows.length} Invoice`}
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-2">
            {results.map(r => {
              const asal = submitted.find(x => x.id === r.id);
              return (
                <div key={r.id} data-testid={`piu-bulk-result-${r.id}`}
                  className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border text-sm ${r.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-700"}`}>
                  <span className="font-medium">{asal?.code || r.id} — {asal?.pelanggan_nama}</span>
                  {r.ok ? <span>{idr(r.nominal)} ✓</span> : <span className="text-xs text-right">{r.error}</span>}
                </div>
              );
            })}
            <button onClick={onClose} data-testid="piu-bulk-tutup" className="w-full h-10 rounded-lg bg-slate-900 text-white text-sm font-medium mt-3">Tutup</button>
          </div>
        )}
        <style>{`.input { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .input:focus { border-color: #059669; box-shadow: 0 0 0 3px rgba(5,150,105,0.1); }`}</style>
      </div>
    </div>
  );
}

// ============ Rekap per Pelanggan (poin 2) ============
function RekapPelanggan({ kasBank }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total_piutang_aktif: 0, count: 0 });
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let batal = false;
    setLoading(true);
    // Debounce ringan supaya kotak cari tidak memicu satu request per ketukan.
    const h = setTimeout(async () => {
      try {
        const r = await api.get("/finance/piutang/pelanggan/ringkasan", q ? { params: { q } } : {});
        if (batal) return;
        setItems(r.data.items || []); setStats(r.data);
      } catch { if (!batal) toast.error("Gagal memuat rekap pelanggan"); }
      finally { if (!batal) setLoading(false); }
    }, 300);
    return () => { batal = true; clearTimeout(h); };
  }, [q]);

  const openDetail = async (nama) => {
    try {
      const r = await api.get("/finance/piutang/pelanggan/detail", { params: { nama } });
      setDetail(r.data);
    } catch { toast.error("Gagal memuat rincian pelanggan"); }
  };

  // Beda dengan tabel invoice di atas: baris di sini adalah hasil GROUP BY
  // nama pelanggan (backend meringkas SEMUA piutang aktif jadi satu baris
  // per pelanggan), bukan potongan N-teratas dari daftar mentah — jadi tidak
  // perlu `BatasDaftarNotice`, sort di sini aman tanpa penanda.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedItems = useMemo(() => sortRows(items, sortKey, sortDir, {
    pelanggan: (g) => g.pelanggan_nama || "",
    invoice_aktif: (g) => g.jumlah_invoice_aktif || 0,
    jatuh_tempo: (g) => g.jumlah_jatuh_tempo || 0,
    total: (g) => g.total_piutang_aktif || 0,
  }), [items, sortKey, sortDir]);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-3 flex items-center gap-2">
        <Search className="w-4 h-4 text-slate-400 shrink-0" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Cari nama pelanggan..."
          data-testid="piu-rekap-search" className="flex-1 h-9 px-2 outline-none text-sm" />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <SortTh label="Pelanggan" sortKeyName="pelanggan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="piu-rekap-sort-pelanggan" />
                <SortTh label="Invoice Aktif" sortKeyName="invoice_aktif" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="piu-rekap-sort-invoice" />
                <SortTh label="Jatuh Tempo" sortKeyName="jatuh_tempo" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="piu-rekap-sort-jt" />
                <SortTh label="Total Piutang Aktif" sortKeyName="total" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="piu-rekap-sort-total" />
                <th className="px-4 py-2.5 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="p-10 text-center text-sm text-slate-400">Memuat...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="p-10 text-center text-sm text-slate-400">Belum ada pelanggan dengan piutang berjalan</td></tr>
              ) : sortedItems.map(g => (
                <tr key={g.pelanggan_nama} data-testid={`piu-rekap-row-${g.pelanggan_nama}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-medium text-slate-900">{g.pelanggan_nama}</td>
                  <td className="px-4 py-3 text-center text-slate-700">{g.jumlah_invoice_aktif}</td>
                  <td className="px-4 py-3 text-center">
                    {g.jumlah_jatuh_tempo > 0 ? <span className="text-rose-600 font-semibold">{g.jumlah_jatuh_tempo}</span> : <span className="text-slate-400">-</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{idr(g.total_piutang_aktif)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openDetail(g.pelanggan_nama)} data-testid={`piu-rekap-detail-${g.pelanggan_nama}`} className="text-xs font-medium text-emerald-700 hover:underline">
                      Lihat Rincian
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {items.length > 0 && (
              <tfoot className="bg-slate-50 border-t border-slate-200 font-semibold text-sm">
                <tr>
                  <td className="px-4 py-2.5" colSpan={3}>Total ({stats.count} pelanggan)</td>
                  <td className="px-4 py-2.5 text-right font-mono">{idr(stats.total_piutang_aktif)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      {detail && <RekapDetailModal detail={detail} kasBank={kasBank} onClose={() => setDetail(null)} />}
    </div>
  );
}

function RekapDetailModal({ detail, kasBank, onClose }) {
  const [expandedId, setExpandedId] = useState(null);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose} data-testid="piu-rekap-modal">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-3xl w-full p-6 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display font-bold text-lg text-slate-900">{detail.pelanggan_nama}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400 hover:text-slate-700" /></button>
        </div>
        <p className="text-sm text-slate-600 mb-4">{detail.count} invoice · Piutang aktif {idr(detail.total_piutang_aktif)}</p>
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Kode / Tanggal</th>
                <th className="text-left px-3 py-2">Kategori</th>
                <th className="text-left px-3 py-2">Jatuh Tempo</th>
                <th className="text-right px-3 py-2">Nominal</th>
                <th className="text-right px-3 py-2">Sisa</th>
                <th className="text-center px-3 py-2">Status</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(detail.items || []).length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-xs text-slate-400">Tidak ada invoice</td></tr>
              ) : detail.items.map(p => {
                const sisa = (p.nominal || 0) - (p.nominal_terbayar || 0);
                const isExpanded = expandedId === p.id;
                const payCount = (p.payments || []).length;
                return (
                  <Fragment key={p.id}>
                    <tr className="border-t border-slate-50">
                      <td className="px-3 py-2"><p className="font-mono text-xs text-slate-500">{p.code}</p><p className="text-xs text-slate-600">{p.tanggal}</p></td>
                      <td className="px-3 py-2 text-xs">{p.kategori}</td>
                      <td className="px-3 py-2 text-xs">{p.tanggal_jatuh_tempo || "-"}</td>
                      <td className="px-3 py-2 text-right font-mono">{idr(p.nominal)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{idr(sisa)}</td>
                      <td className="px-3 py-2 text-center"><StatusPill s={p.status_tampilan || p.status} /></td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => setExpandedId(isExpanded ? null : p.id)} data-testid={`piu-rekap-riwayat-${p.id}`} className="text-xs text-emerald-700 hover:underline">
                          Riwayat{payCount > 0 && ` (${payCount})`}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr><td colSpan={7} className="px-4 py-3 bg-slate-50/60"><RiwayatPembayaran payments={p.payments} kasBank={kasBank} /></td></tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** Rekening tujuan pembayaran per cabang — yang dicetak di surat tagihan
 *  (brief 29 Agu poin 22).
 *
 *  Cabang yang BELUM diisi tetap ditampilkan dengan kolom kosong, dan
 *  jumlahnya disebut di atas. Kalau yang belum diisi disembunyikan, tidak akan
 *  ada yang tahu bahwa surat tagihan untuk cabang itu keluar tanpa nomor
 *  rekening — dan itu baru ketahuan dari pelanggan yang bingung mau transfer
 *  ke mana. */
function RekeningCabangModal({ onClose }) {
  const { can } = useAuth();
  // Kunci SAMA dengan `PUT /finance/rekening-cabang` (finance_piutang.update).
  // Pemegang `view` saja tetap boleh MELIHAT rekeningnya (GET = view), tapi
  // tombol Simpan tidak ditampilkan — kalau ditampilkan, ujungnya 403
  // (aturan 3 CLAUDE.md).
  const bolehUbah = can("finance_piutang", "update");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  // F9 (brief 12 Sep): rekening ZOA GROUP dipakai sebagai CADANGAN saat
  // rekening cabang kosong. Server yang menentukan mana baris pusat
  // (`pusat: true`) dan mana cabang toko — layar tidak menebaknya dari nama.
  const [pusatTerisi, setPusatTerisi] = useState(false);

  const muat = async () => {
    try {
      const r = await api.get("/finance/rekening-cabang");
      setRows(r.data.items || []);
      setPusatTerisi(!!r.data.pusat_terisi);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat rekening cabang");
      onClose();
    }
  };
  useEffect(() => {
    muat();
    // Sekali saat modal dibuka. `muat` sengaja tidak masuk dependensi:
    // ia dibuat ulang tiap render, dan memasukkannya membuat efek ini
    // berjalan tanpa henti.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ubah = (cabang, medan, nilai) =>
    setRows(rs => rs.map(r => (r.cabang === cabang ? { ...r, [medan]: nilai } : r)));

  const simpan = async (r) => {
    setBusy(r.cabang);
    try {
      await api.put("/finance/rekening-cabang", {
        cabang: r.cabang, bank: r.bank || "", nomor: r.nomor || "",
        atas_nama: r.atas_nama || "",
      });
      toast.success(`Rekening ${r.cabang} disimpan`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setBusy(null); }
  };

  const belum = (rows || []).filter(r => !r.nomor && !r.pusat).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}
         data-testid="piu-rekening-modal">
      <div onClick={e => e.stopPropagation()}
           className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-lg">Rekening Tagihan per Cabang</h3>
            <p className="text-xs text-slate-500">Dicetak di surat tagihan sebagai tujuan pembayaran.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {rows === null ? (
            <p className="text-sm text-slate-400 text-center py-8">Memuat...</p>
          ) : (
            <>
              {belum > 0 && (
                <p className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                   data-testid="piu-rekening-belum">
                  {belum} cabang belum punya nomor rekening.{" "}
                  {pusatTerisi
                    ? <>Surat tagihannya akan memakai <b>rekening ZOA GROUP</b> dan menyebutkannya
                        apa adanya sebagai rekening pusat. Begitu rekening cabangnya diisi, yang
                        dipakai kembali rekening cabang itu sendiri.</>
                    : <>Surat tagihan untuk cabang itu akan menyebutkan bahwa rekeningnya belum
                        diisi — bukan memakai rekening cabang lain. Isi <b>ZOA GROUP</b> di bawah
                        kalau ingin dipakai sebagai rekening cadangan.</>}
                </p>
              )}
              {!bolehUbah && (
                <p className="mb-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
                   data-testid="piu-rekening-hanya-lihat">
                  Hanya-lihat — mengubah rekening tagihan butuh izin Edit Piutang.
                </p>
              )}
              <div className="space-y-2">
                {rows.map(r => (
                  <div key={r.cabang} className="grid grid-cols-12 gap-2 items-center"
                       data-testid={`piu-rekening-${r.cabang}`}>
                    <p className="col-span-12 md:col-span-3 text-sm font-medium text-slate-800">
                      {r.cabang}
                      {r.pusat && (
                        <span className="ml-1.5 text-[10px] font-normal px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-100"
                              title="Dipakai kalau rekening cabang penjual belum diisi">
                          cadangan pusat
                        </span>
                      )}
                    </p>
                    <input value={r.bank || ""} onChange={e => ubah(r.cabang, "bank", e.target.value)}
                      readOnly={!bolehUbah}
                      placeholder="Bank" className="col-span-4 md:col-span-2 h-9 px-2 rounded-lg border border-slate-200 text-sm read-only:bg-slate-50" />
                    <input value={r.nomor || ""} onChange={e => ubah(r.cabang, "nomor", e.target.value)}
                      readOnly={!bolehUbah}
                      placeholder="Nomor rekening" className="col-span-8 md:col-span-3 h-9 px-2 rounded-lg border border-slate-200 text-sm font-mono read-only:bg-slate-50" />
                    <input value={r.atas_nama || ""} onChange={e => ubah(r.cabang, "atas_nama", e.target.value)}
                      readOnly={!bolehUbah}
                      placeholder="Atas nama" className="col-span-8 md:col-span-3 h-9 px-2 rounded-lg border border-slate-200 text-sm read-only:bg-slate-50" />
                    {bolehUbah && (
                      <button onClick={() => simpan(r)} disabled={busy === r.cabang}
                        data-testid={`piu-rekening-simpan-${r.cabang}`}
                        className="col-span-4 md:col-span-1 h-9 px-2 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-xs font-medium">
                        {busy === r.cabang ? "..." : "Simpan"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function ExportDialog({ module, onClose }) {
  // `hariIniISO()` untuk keduanya — `first.toISOString()` sebelumnya UTC dan
  // bisa memberi tanggal 1 bulan yang salah untuk pengguna WIB sebelum jam
  // 07:00 (lihat frontend/src/lib/tanggal.js).
  const today = hariIniISO();
  const [start, setStart] = useState(`${today.slice(0, 7)}-01`);
  const [end, setEnd] = useState(today);
  const [busy, setBusy] = useState(false);
  const doExport = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("hris_token");
      const res = await fetch(`${API}/finance/${module}/export-xlsx?start_date=${start}&end_date=${end}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export gagal");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${module}-${start}_sd_${end}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel berhasil di-download 📊");
      onClose();
    } catch (e) { toast.error("Gagal export"); }
    finally { setBusy(false); }
  };
  const label = module === "piutang" ? "Piutang" : "Utang";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-emerald-600" /> Export {label} ke Excel</h3>
        <p className="text-xs text-slate-500 mb-4">3 sheet by status: Belum Lunas · Jatuh Tempo · Lunas + Ringkasan</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Tanggal Mulai</label>
            <input type="date" value={start} onChange={e => setStart(e.target.value)} data-testid="exp-start" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 outline-none" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Tanggal Akhir</label>
            <input type="date" value={end} onChange={e => setEnd(e.target.value)} data-testid="exp-end" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 outline-none" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={doExport} disabled={busy} data-testid="exp-submit" className="flex-1 h-10 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50">{busy ? "Generating..." : "Download"}</button>
        </div>
      </div>
    </div>
  );
}


function ModalInvestigasi({ piutang, onBatal, onKirim }) {
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState(false);
  const selisih = piutang.selisih_nominal || 0;

  // Alasan WAJIB — penanda investigasi tanpa alasan cuma memberi tahu bahwa dulu
  // ada yang curiga, tanpa memberi tahu curiga apa. Backend juga menolaknya, jadi
  // tombolnya dimatikan di sini supaya tidak ada klik yang berujung 400.
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onBatal}>
      <div onClick={e => e.stopPropagation()} data-testid="piu-investigasi-modal"
           className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-lg">Tandai Perlu Investigasi</h3>
          <button onClick={onBatal} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="bg-slate-50 rounded-lg p-3 text-sm mb-3">
          <p className="font-medium text-slate-900">{piutang.pelanggan_nama}</p>
          <p className="text-xs text-slate-500 font-mono">{piutang.code}</p>
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-slate-500">Tagihan</span><p className="font-mono">{idr(piutang.nominal)}</p></div>
            <div><span className="text-slate-500">Diterima</span><p className="font-mono">{idr(piutang.nominal_terbayar || 0)}</p></div>
            <div><span className="text-slate-500">Selisih</span>
              <p className="font-mono text-rose-600">{idr(selisih)} ({(piutang.selisih_persen || 0).toFixed(1)}%)</p></div>
            <div><span className="text-slate-500">SLA</span><p>{piutang.sla_label || "-"}</p></div>
          </div>
        </div>
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
          Alasan investigasi (wajib)
        </label>
        <textarea value={catatan} onChange={e => setCatatan(e.target.value)} rows={3}
                  data-testid="piu-investigasi-catatan"
                  placeholder="Contoh: transfer kurang Rp 100.000, sedang dikonfirmasi ke pelanggan"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-amber-500" />
        <div className="flex gap-2 mt-4">
          <button onClick={onBatal} className="flex-1 h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Batal</button>
          <button disabled={busy || !catatan.trim()} data-testid="piu-investigasi-kirim"
                  onClick={async () => { setBusy(true); try { await onKirim(catatan.trim()); } finally { setBusy(false); } }}
                  className="flex-1 h-10 rounded-lg bg-amber-600 text-white hover:bg-amber-700 text-sm font-medium disabled:opacity-50">
            {busy ? "..." : "Tandai Investigasi"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ s }) {
  const map = {
    aktif:             { c: "bg-sky-50 text-sky-700 border-sky-200", i: Clock, l: "Aktif" },
    // Field turunan (poin 9 brief owner 17 Agu 2026) — status TERSIMPAN di
    // dokumen tetap "aktif" (lihat `_shared._status_tampilan_ap_ar`), ini
    // cuma label tampilan tambahan untuk piutang yang sudah dibayar sebagian.
    terbayar_parsial:  { c: "bg-amber-50 text-amber-700 border-amber-200", i: Clock, l: "Terbayar Parsial" },
    lunas:             { c: "bg-emerald-50 text-emerald-700 border-emerald-200", i: CheckCircle2, l: "Lunas" },
    pending_approval:  { c: "bg-amber-50 text-amber-800 border-amber-200", i: AlertTriangle, l: "Pending SPV" },
    rejected:          { c: "bg-rose-50 text-rose-700 border-rose-200", i: X, l: "Ditolak" },
    // Piutang yang lahir OTOMATIS dari nota bertempo (owner 29 Agu 2026).
    // Statusnya sendiri yang memberi tahu apa yang ditunggu — tanpa itu,
    // tagihan yang belum diperiksa terlihat sama dengan yang siap ditagih.
    belum_diverifikasi: { c: "bg-violet-50 text-violet-700 border-violet-200", i: AlertTriangle, l: "Belum Diverifikasi" },
    perlu_perbaikan:   { c: "bg-orange-50 text-orange-700 border-orange-200", i: AlertTriangle, l: "Perlu Perbaikan" },
    dibatalkan:        { c: "bg-slate-100 text-slate-600 border-slate-200", i: X, l: "Dibatalkan" },
  };
  const it = map[s] || map.aktif; const Icon = it.i;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${it.c}`}><Icon className="w-3 h-3" /> {it.l}</span>;
}

function FormPiutang({ kategori, onClose }) {
  const [form, setForm] = useState({ tanggal: hariIniISO(), pelanggan_nama: "", no_hp: "", nominal: "", kategori: "", tanggal_jatuh_tempo: "", brand: "", keterangan: "" });
  const [busy, setBusy] = useState(false);

  const handleOcr = (d) => {
    setForm(f => ({
      ...f,
      tanggal: d.tanggal || f.tanggal,
      nominal: d.nominal != null ? String(d.nominal) : f.nominal,
      pelanggan_nama: d.pelanggan_nama || f.pelanggan_nama,
      keterangan: d.keterangan || f.keterangan,
    }));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/finance/piutang", { ...form, nominal: Number(form.nominal) });
      toast.success(r.data.message); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><HandCoins className="w-5 h-5 text-emerald-600" /> Piutang Baru</h3>
          <OcrScanButton mode="piutang" onExtracted={handleOcr} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tanggal"><input type="date" value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })} className="input" /></Field>
          <Field label="Jatuh Tempo"><input type="date" value={form.tanggal_jatuh_tempo} onChange={e => setForm({ ...form, tanggal_jatuh_tempo: e.target.value })} className="input" /></Field>
          <Field label="Nama Pelanggan" col2><input value={form.pelanggan_nama} onChange={e => setForm({ ...form, pelanggan_nama: e.target.value })} className="input" data-testid="piu-form-nama" /></Field>
          <Field label="No HP" col2><input value={form.no_hp} onChange={e => setForm({ ...form, no_hp: e.target.value })} className="input" /></Field>
          <Field label="Kategori"><select value={form.kategori} onChange={e => setForm({ ...form, kategori: e.target.value })} className="input" data-testid="piu-form-kat"><option value="">Pilih...</option>{kategori.map(k => <option key={k} value={k}>{k}</option>)}</select></Field>
          <Field label="Nominal"><InputRupiah value={form.nominal} onChange={n => setForm({ ...form, nominal: n })} className="input" testid="piu-form-nom" /></Field>
          <Field label="Keterangan" col2><textarea rows={2} value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} className="input" /></Field>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={submit} disabled={busy || !form.pelanggan_nama || !form.nominal || !form.kategori} data-testid="piu-form-submit" className="flex-1 h-10 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Simpan"}</button>
        </div>
      </div>
    </div>
  );
}

function FormBayar({ piutang, kasBank, onClose }) {
  const sisa = (piutang.nominal || 0) - (piutang.nominal_terbayar || 0);
  const [form, setForm] = useState({ tanggal: hariIniISO(), nominal: sisa, sumber_akun_id: "", keterangan: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/finance/piutang/${piutang.id}/bayar`, { ...form, nominal: Number(form.nominal) });
      toast.success(r.data.message); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal bayar"); }
    finally { setBusy(false); }
  };
  // `dangerous` — nominal pembayaran diketik/dipilih staf sendiri (default
  // dari sisa, tapi bisa diubah), plus pilihan akun tujuan.
  const guard = useModalGuard({ onClose, onSubmit: submit, busy, disabled: busy || !form.sumber_akun_id || !form.nominal, dangerous: true });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={e => e.stopPropagation()} data-testid="piu-bayar-modal" className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="font-display font-bold text-lg mb-1">Bayar Piutang</h3>
        <p className="text-sm text-slate-600 mb-4">{piutang.pelanggan_nama} · Sisa {idr(sisa)}</p>
        <div className="space-y-3">
          <Field label="Tanggal"><input type="date" value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })} className="input" /></Field>
          <Field label="Nominal Bayar"><InputRupiah value={form.nominal} onChange={n => setForm({ ...form, nominal: n })} className="input" testid="piu-bayar-nom" /></Field>
          <Field label="Masuk ke Akun"><select value={form.sumber_akun_id} onChange={e => setForm({ ...form, sumber_akun_id: e.target.value })} className="input" data-testid="piu-bayar-akun"><option value="">Pilih...</option>{kasBank.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></Field>
          <Field label="Keterangan"><input value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} className="input" /></Field>
        </div>
        {guard.confirming && (
          <div className="mt-3 -mx-6"><UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid="piu-bayar-guard" /></div>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={guard.requestClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={submit} disabled={busy || !form.sumber_akun_id || !form.nominal} data-testid="piu-bayar-submit" className="flex-1 h-10 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Bayar"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, col2 }) {
  return (
    <div className={col2 ? "col-span-2" : ""}>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
      <style>{`.input { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .input:focus { border-color: #059669; box-shadow: 0 0 0 3px rgba(5,150,105,0.1); } textarea.input { height: auto; padding: 10px 12px; }`}</style>
    </div>
  );
}
